import { useQuery } from "@tanstack/react-query";
import { ApiResponse, IEntity } from "common/models/ApiResponse";
import React, {
  PropsWithChildren,
  createContext,
  useEffect,
  useState,
} from "react";

export interface ModalProp<TModel extends IEntity> {
  open: boolean;
  id: string | number;
  isFetching: boolean;
  model?: TModel;
}

export interface ModalViewProps<TModel extends IEntity, TId = string | number> {
  open: boolean;
  id: TId;
  queryKey?: string;
  modalFetcher: (id: any) => Promise<ApiResponse<TModel>>;
  Component: React.FC<ModalProp<TModel>>;
}

export function ModalView<TModel extends IEntity>({
  open,
  id,
  queryKey,
  modalFetcher,
  Component,
}: ModalViewProps<TModel>) {
  const [model, setModel] = useState<TModel>();

  const query = useQuery({
    queryKey: [queryKey, "modal", id],
    queryFn: () => modalFetcher(id),
    enabled: Boolean(id),
  });

  useEffect(() => {
    if (query.data) {
      setModel(query.data.apiData);
    }
  }, [query.data]);

  return (
    <Component
      open={open}
      isFetching={query.isFetching}
      id={id}
      model={model}
    />
  );
}

interface PromiseInfo {
  resolve: (value: boolean | PromiseLike<boolean>) => void;
  reject: (reason?: any) => void;
}

type ShowDialogLoaderHandler = ({ id }: { id: string | number }) => Promise<boolean>;

interface ContextType<TModel extends IEntity> {
  data?: TModel;
  showDialog: ShowDialogLoaderHandler;
  handleCancel: () => void;
  handleConfirm: () => void;
}

const DialogLoaderContext = createContext<ContextType<IEntity> | null>(null);

export function useDialogLoader<TEntity extends IEntity>() {
  const context = React.useContext<ContextType<TEntity>>(
    DialogLoaderContext as unknown as React.Context<ContextType<TEntity>>
  );
  if (!context) {
    throw new Error("useDialogLoader must be used under DialogLoaderContext");
  }
  return context;
}

type ContextProps<TModel extends IEntity> = {
  title: string;
  message?: string;
  queryKey?: string;
  modalFetcher: (id: any) => Promise<ApiResponse<TModel>>;
  Component: React.FC<ModalProp<TModel>>;
};

export default function DialogLoaderProvider<TModel extends IEntity>({
  modalFetcher,
  queryKey,
  Component,
  children,
}: PropsWithChildren<ContextProps<TModel>>) {
  const [open, setOpen] = useState(false);
  const [currentId, setCurrentId] = useState<string | number>(0);

  const [promiseInfo, setPromiseInfo] = useState<PromiseInfo>();
  const showDialog: ShowDialogLoaderHandler = (data) => {
    setCurrentId(data.id);
    return new Promise<boolean>((resolve, reject) => {
      setPromiseInfo({ resolve, reject });
      setOptions(options);
      setOpen(true);
    });
  };

  const handleConfirm = () => {
    setOpen(false);
    promiseInfo?.resolve(true);
    setPromiseInfo(undefined);
  };

  const handleCancel = () => {
    setOpen(false);
    promiseInfo?.resolve(false);
    setPromiseInfo(undefined);
  };

  const [options, setOptions] = useState<ContextType<IEntity>>({
    data: "sukhdev",
    showDialog,
    handleCancel,
    handleConfirm,
  });

  return (
    <>
      <DialogLoaderContext.Provider value={options}>
        {children}
        <ModalView
          open={open}
          id={currentId}
          Component={Component}
          queryKey={queryKey}
          modalFetcher={modalFetcher}
        />
      </DialogLoaderContext.Provider>
    </>
  );
}
